/**
 * Datadog RUM bootstrap.
 *
 * Everything is driven by REACT_APP_DD_* environment variables (inlined by CRA at build time).
 * Nothing is loaded or sent unless REACT_APP_DD_ENABLE=true AND both credentials are present.
 *
 * Required when enabled:
 *   REACT_APP_DD_ENABLE=true
 *   REACT_APP_DD_APP_ID=<Datadog RUM application id>
 *   REACT_APP_DD_CLIENT_TOKEN=<Datadog RUM client token>
 *
 * Optional (defaults shown are tuned for production on a hosted payment page):
 *   REACT_APP_DD_SITE=datadoghq.com
 *   REACT_APP_DD_SERVICE=daspay-hpp-v2
 *   REACT_APP_DD_ENV=production
 *   REACT_APP_DD_VERSION=<release/commit sha shown in Datadog>
 *   REACT_APP_DD_SESSION_SAMPLE_RATE=100        (0-100, % of sessions tracked)
 *   REACT_APP_DD_REPLAY_SAMPLE_RATE=5           (0-100, % of tracked sessions recorded)
 *   REACT_APP_DD_TRACE_SAMPLE_RATE=20           (0-100, % of API calls traced to APM)
 *   REACT_APP_DD_PRIVACY_LEVEL=mask             (allow | mask | mask-user-input) - card data page, mask everything
 *   REACT_APP_DD_TRACK_INTERACTIONS=true
 *   REACT_APP_DD_TRACK_RESOURCES=true
 *   REACT_APP_DD_TRACK_LONG_TASKS=true
 *   REACT_APP_DD_COMPRESS=true                  (compress intake requests; needs worker-src blob: if you use CSP)
 *   REACT_APP_DD_TRACING_URLS=<comma separated list; defaults to REACT_APP_PUBLIC_API_URL>
 */
import type { DefaultPrivacyLevel, PropagatorType } from '@datadog/browser-rum';

const env = process.env;

const isTrue = (value: string | undefined, fallback: boolean): boolean => {
  if (value === undefined || value === '') return fallback;
  return value.trim().toLowerCase() === 'true';
};

const toRate = (value: string | undefined, fallback: number): number => {
  const n = Number(value);
  if (value === undefined || value === '' || Number.isNaN(n)) return fallback;
  return Math.min(100, Math.max(0, n));
};

const toPrivacyLevel = (value: string | undefined): DefaultPrivacyLevel => {
  const allowed: DefaultPrivacyLevel[] = ['allow', 'mask', 'mask-user-input'];
  return allowed.includes(value as DefaultPrivacyLevel)
    ? (value as DefaultPrivacyLevel)
    : 'mask';
};

const toList = (value: string | undefined): string[] =>
  (value ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

export const isDatadogEnabled = (): boolean =>
  isTrue(env.REACT_APP_DD_ENABLE, false) &&
  Boolean(env.REACT_APP_DD_APP_ID) &&
  Boolean(env.REACT_APP_DD_CLIENT_TOKEN);

let initialised = false;

/**
 * Initialise Datadog RUM. Safe to call multiple times; no-op when disabled or misconfigured.
 * The SDK is imported dynamically so it is never part of the main bundle when disabled.
 */
export const initDatadogRum = async (): Promise<void> => {
  if (initialised) return;

  if (!isDatadogEnabled()) {
    if (isTrue(env.REACT_APP_DD_ENABLE, false)) {
      // eslint-disable-next-line no-console
      console.warn(
        '[datadog] REACT_APP_DD_ENABLE is true but REACT_APP_DD_APP_ID / REACT_APP_DD_CLIENT_TOKEN are missing. RUM not started.',
      );
    }
    return;
  }

  initialised = true;

  try {
    const { datadogRum } = await import(
      /* webpackChunkName: "datadog-rum" */ '@datadog/browser-rum'
    );

    const propagatorTypes: PropagatorType[] = ['tracecontext', 'datadog'];
    const tracingUrls = toList(env.REACT_APP_DD_TRACING_URLS);
    if (tracingUrls.length === 0 && env.REACT_APP_PUBLIC_API_URL) {
      tracingUrls.push(env.REACT_APP_PUBLIC_API_URL);
    }

    datadogRum.init({
      applicationId: env.REACT_APP_DD_APP_ID as string,
      clientToken: env.REACT_APP_DD_CLIENT_TOKEN as string,
      site: env.REACT_APP_DD_SITE || 'datadoghq.com',
      service: env.REACT_APP_DD_SERVICE || 'daspay-hpp-v2',
      env: env.REACT_APP_DD_ENV || env.NODE_ENV || 'production',
      version: env.REACT_APP_DD_VERSION || undefined,

      // Sampling: keep prod volume/cost under control, override per environment via env.
      sessionSampleRate: toRate(env.REACT_APP_DD_SESSION_SAMPLE_RATE, 100),
      sessionReplaySampleRate: toRate(env.REACT_APP_DD_REPLAY_SAMPLE_RATE, 5),
      traceSampleRate: toRate(env.REACT_APP_DD_TRACE_SAMPLE_RATE, 20),

      // Only propagate trace headers to our own API (avoids CORS failures on
      // third parties such as Cardinal, Google Pay, SilverExpress).
      allowedTracingUrls: tracingUrls.map((match) => ({ match, propagatorTypes })),

      // Payment page: mask all text/inputs in replays unless explicitly relaxed.
      defaultPrivacyLevel: toPrivacyLevel(env.REACT_APP_DD_PRIVACY_LEVEL),
      trackUserInteractions: isTrue(env.REACT_APP_DD_TRACK_INTERACTIONS, true),
      trackResources: isTrue(env.REACT_APP_DD_TRACK_RESOURCES, true),
      trackLongTasks: isTrue(env.REACT_APP_DD_TRACK_LONG_TASKS, true),

      // Smaller payloads = less bandwidth on the user's device.
      compressIntakeRequests: isTrue(env.REACT_APP_DD_COMPRESS, true),
      // Don't ship the SDK's own diagnostics in production.
      telemetrySampleRate: env.NODE_ENV === 'production' ? 0 : 20,
    });
  } catch (error) {
    initialised = false;
    if (env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('[datadog] Failed to initialise RUM', error);
    }
  }
};
